import { z } from 'zod'
import { uuidSchema } from '../validators/uuid.validator.js'
import { proposalWorkSchema } from '../enums/proposal-work.enum.js'

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullish()

/**
 * Create a proposal. Creating one always creates version 1 alongside it — a
 * proposal with no versions would be a container with nothing in it, and no
 * screen would know what to show.
 *
 * `siteIds` can hold several: a job across an HOA's parks is one proposal.
 */
export const createProposalInputSchema = z
  .object({
    clientId: uuidSchema,
    title: z.string().trim().min(1, 'Title is required').max(200),
    ownerId: uuidSchema.nullish(),
    summary: optionalText(20000),
    siteIds: z.array(uuidSchema).max(50).optional(),
  })
  .strict()

export type CreateProposalInput = z.infer<typeof createProposalInputSchema>

export const updateProposalInputSchema = z
  .object({
    title: z.string().trim().min(1, 'Title is required').max(200).optional(),
    ownerId: uuidSchema.nullish(),
    /** Break the price out by area name. Omitted leaves the choice as it is. */
    itemizeByArea: z.boolean().optional(),
    /** What it bids. Omitted leaves the choice as it is. */
    work: proposalWorkSchema.optional(),
  })
  .strict()

export type UpdateProposalInput = z.infer<typeof updateProposalInputSchema>

/**
 * Edit a draft version. Rejected by the API once the version has been sent —
 * revising a sent proposal means creating the next version, not rewriting
 * history.
 */
export const updateProposalVersionInputSchema = z
  .object({
    summary: optionalText(20000),
    /** ISO date. Null clears the expiry. */
    validUntil: z.string().datetime().nullish(),
    siteIds: z.array(uuidSchema).max(50).optional(),
    documentIds: z.array(uuidSchema).max(100).optional(),
  })
  .strict()

export type UpdateProposalVersionInput = z.infer<
  typeof updateProposalVersionInputSchema
>

/**
 * Create the next version of an existing proposal. Defaults to copying the
 * current version's content forward, because a revision is almost always "the
 * last one, with changes" rather than a blank page.
 */
export const createProposalVersionInputSchema = z
  .object({
    copyFromLatest: z.boolean().optional(),
    summary: optionalText(20000),
  })
  .strict()

export type CreateProposalVersionInput = z.infer<
  typeof createProposalVersionInputSchema
>

/** The client's answer to a sent version. */
export const PROPOSAL_VERSION_DECISIONS = ['ACCEPTED', 'DECLINED'] as const

export const proposalVersionDecisionSchema = z.enum(PROPOSAL_VERSION_DECISIONS)

export type ProposalVersionDecision = z.infer<
  typeof proposalVersionDecisionSchema
>

/** Record the client's answer to a sent version. */
export const proposalVersionDecisionInputSchema = z
  .object({
    decision: proposalVersionDecisionSchema,
  })
  .strict()

export type ProposalVersionDecisionInput = z.infer<
  typeof proposalVersionDecisionInputSchema
>

/** Query params for the proposal list. */
export const listProposalsQuerySchema = z
  .object({
    clientId: uuidSchema.optional(),
    search: z.string().trim().max(200).optional(),
    includeArchived: z
      .enum(['true', 'false'])
      .optional()
      .transform((value) => value === 'true'),
  })
  .strict()

export type ListProposalsQuery = z.infer<typeof listProposalsQuerySchema>
